// /admin/beats — Season 1 narrative-beat publisher.
//
// Each row carries a status pill, beat metadata (id + title + summary
// preview + letter_type_bias + triggers_unstable flag), and a single
// primary action that morphs by status (Publish for drafts, kebab
// menu for published with Unpublish inside). The kebab's Unpublish is
// the only destructive action in the whole admin surface and the only
// thing that opens a confirm modal — everything else commits inline.

import {
  type AdminBeat,
  type AdminBondSummary,
  type BeatEditPayload,
  type PreviewBroadcastResult,
  type PublishBeatResult,
  editBeat,
  getBroadcastPreview,
  listAdminBeats,
  listAdminBonds,
  previewBroadcastForBond,
  publishBeat,
  setBeatActivationMode,
  unpublishBeat,
} from '../lib/api';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/admin/beats')({
  loader: async () => {
    const beats = await listAdminBeats();
    return { beats };
  },
  component: BeatsPage,
});

function BeatsPage() {
  const initial = Route.useLoaderData();
  const router = useRouter();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [confirmUnpublish, setConfirmUnpublish] = useState<AdminBeat | null>(null);
  const [confirmBroadcast, setConfirmBroadcast] = useState<AdminBeat | null>(null);
  const [editing, setEditing] = useState<AdminBeat | null>(null);
  const [lastPublishResult, setLastPublishResult] = useState<{
    beatId: string;
    enqueued: number;
    skippedExisting: number;
  } | null>(null);

  async function handlePublishPassive(beat: AdminBeat) {
    setPendingId(beat.id);
    try {
      await publishBeat(beat.id);
      await router.invalidate();
    } finally {
      setPendingId(null);
    }
  }

  async function handleConfirmBroadcastPublish(beat: AdminBeat) {
    setPendingId(beat.id);
    try {
      const result: PublishBeatResult = await publishBeat(beat.id);
      setLastPublishResult({
        beatId: beat.beatId,
        enqueued: result.broadcasts.enqueued,
        skippedExisting: result.broadcasts.skippedExisting,
      });
      await router.invalidate();
    } finally {
      setPendingId(null);
      setConfirmBroadcast(null);
    }
  }

  async function handleUnpublish(beat: AdminBeat) {
    setPendingId(beat.id);
    try {
      await unpublishBeat(beat.id);
      await router.invalidate();
    } finally {
      setPendingId(null);
      setConfirmUnpublish(null);
    }
  }

  async function handleToggleMode(beat: AdminBeat, next: 'passive' | 'broadcast') {
    if (beat.activationMode === next) return;
    setPendingId(beat.id);
    try {
      await setBeatActivationMode(beat.id, next);
      await router.invalidate();
    } finally {
      setPendingId(null);
    }
  }

  async function handleSaveEdit(beat: AdminBeat, payload: BeatEditPayload) {
    setPendingId(beat.id);
    try {
      await editBeat(beat.id, payload);
      await router.invalidate();
    } finally {
      setPendingId(null);
      setEditing(null);
    }
  }

  return (
    <section>
      <header className="mb-6">
        <h2 className="font-display text-2xl text-ash-50 tracking-wide">Season 1 beats</h2>
        <p className="mt-1 font-body text-ash-500 text-sm italic">
          Publishing a beat sets it live for {30} days and lets bonds with matching context
          surface its letter type.
        </p>
      </header>

      {initial.beats.length === 0 ? (
        <p className="font-body text-ash-300">No beats seeded.</p>
      ) : (
        <ul className="space-y-3">
          {initial.beats.map((b) => (
            <li
              key={b.id}
              className="flex flex-col gap-3 rounded-sm border border-paper-800/60 bg-paper-900/30 p-4 sm:flex-row sm:items-start sm:gap-5 lg:p-5"
            >
              <div className="flex-1">
                {/* Top row: status pills + beat id */}
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill status={b.status} />
                  <ModePill mode={b.activationMode} />
                  <SourcePill hasPostUrl={Boolean(b.canonicalPostUrl)} />
                  <span className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
                    {b.beatId}
                  </span>
                </div>

                {/* Title + summary */}
                <h3 className="mt-3 font-display text-ash-50 text-lg leading-snug">{b.title}</h3>
                <p className="mt-1 font-body text-ash-300 text-sm leading-relaxed">{b.summary}</p>

                {/* Bottom chips: bias + triggers + IG link */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <BiasChip bias={b.letterTypeBias} />
                  {b.triggersUnstable && (
                    <span className="rounded-sm border border-accent/40 bg-paper-900/50 px-2 py-0.5 font-display text-[0.58rem] text-accent uppercase tracking-[0.3em]">
                      triggers unstable
                    </span>
                  )}
                  {b.canonicalPostUrl && (
                    <a
                      href={b.canonicalPostUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-display text-[0.58rem] text-ash-400 underline decoration-ash-700 underline-offset-[4px] uppercase tracking-[0.3em] transition hover:text-ash-100 hover:decoration-ash-300"
                    >
                      ↗ canonical post
                    </a>
                  )}
                </div>

                {b.status === 'published' && (
                  <p className="mt-3 font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
                    live until {new Date(b.activeUntil).toISOString().slice(0, 10)}
                  </p>
                )}
                {b.status === 'draft' && (
                  <ModeToggle
                    beat={b}
                    pending={pendingId === b.id}
                    onSelect={(mode) => handleToggleMode(b, mode)}
                  />
                )}
              </div>

              <div className="flex items-center gap-2 self-start">
                {b.status === 'draft' && (
                  <>
                    <button
                      type="button"
                      onClick={() => setEditing(b)}
                      disabled={pendingId === b.id}
                      className="rounded-sm border border-paper-800/60 bg-paper-900/40 px-3 py-2 font-display text-ash-300 text-xs uppercase tracking-[0.32em] transition hover:border-ash-700 hover:text-ash-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={pendingId === b.id}
                      onClick={() =>
                        b.activationMode === 'broadcast'
                          ? setConfirmBroadcast(b)
                          : handlePublishPassive(b)
                      }
                      className="rounded-sm border border-accent/50 bg-paper-900/60 px-4 py-2 font-display text-ash-100 text-xs uppercase tracking-[0.32em] transition hover:border-accent-strong/80 hover:bg-paper-700/60 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {pendingId === b.id ? 'Publishing…' : 'Publish'}
                    </button>
                  </>
                )}
                {b.status === 'published' && (
                  <button
                    type="button"
                    onClick={() => setConfirmUnpublish(b)}
                    className="rounded-sm border border-paper-800/60 bg-paper-900/40 px-4 py-2 font-display text-ash-300 text-xs uppercase tracking-[0.32em] transition hover:border-ash-700 hover:text-ash-100"
                  >
                    Unpublish
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {confirmUnpublish && (
        <UnpublishModal
          beat={confirmUnpublish}
          pending={pendingId === confirmUnpublish.id}
          onConfirm={() => handleUnpublish(confirmUnpublish)}
          onCancel={() => setConfirmUnpublish(null)}
        />
      )}

      {confirmBroadcast && (
        <BroadcastPublishModal
          beat={confirmBroadcast}
          pending={pendingId === confirmBroadcast.id}
          onConfirm={() => handleConfirmBroadcastPublish(confirmBroadcast)}
          onCancel={() => setConfirmBroadcast(null)}
        />
      )}

      {lastPublishResult && (
        <PublishResultBanner
          result={lastPublishResult}
          onDismiss={() => setLastPublishResult(null)}
        />
      )}

      {editing && (
        <EditBeatModal
          beat={editing}
          pending={pendingId === editing.id}
          onSave={(payload) => handleSaveEdit(editing, payload)}
          onCancel={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function SourcePill({ hasPostUrl }: { hasPostUrl: boolean }) {
  // Public = there's a canonical Instagram post (or other public
  // surface) telling this lore to the audience. Internal = the beat
  // exists only in the Ember context, with no outward narration.
  // Both can be passive or broadcast; the source is orthogonal to the
  // activation mode.
  const tone = hasPostUrl
    ? 'border-accent/50 text-accent-strong'
    : 'border-ash-700/60 text-ash-400';
  return (
    <span
      className={`rounded-full border px-2 py-0.5 font-display text-[0.55rem] uppercase tracking-[0.3em] ${tone}`}
    >
      {hasPostUrl ? 'public' : 'internal'}
    </span>
  );
}

function BiasChip({ bias }: { bias: AdminBeat['letterTypeBias'] }) {
  const label = bias ?? 'no bias';
  const tone = bias
    ? 'border-accent/40 text-accent-strong'
    : 'border-paper-800/60 text-ash-500';
  return (
    <span
      className={`rounded-sm border px-2 py-0.5 font-display text-[0.58rem] uppercase tracking-[0.3em] ${tone}`}
    >
      {label}
    </span>
  );
}

function ModePill({ mode }: { mode: AdminBeat['activationMode'] }) {
  const tone =
    mode === 'broadcast'
      ? 'border-accent-strong/60 text-accent-strong'
      : 'border-ash-700/60 text-ash-400';
  return (
    <span
      className={`rounded-full border px-2 py-0.5 font-display text-[0.55rem] uppercase tracking-[0.3em] ${tone}`}
    >
      {mode}
    </span>
  );
}

function ModeToggle({
  beat,
  pending,
  onSelect,
}: {
  beat: AdminBeat;
  pending: boolean;
  onSelect: (mode: 'passive' | 'broadcast') => void;
}) {
  return (
    <div className="mt-3 flex items-center gap-2">
      <span className="font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.32em]">
        On publish:
      </span>
      {(['passive', 'broadcast'] as const).map((m) => (
        <button
          key={m}
          type="button"
          disabled={pending || beat.activationMode === m}
          onClick={() => onSelect(m)}
          className={`rounded-sm border px-2.5 py-1 font-display text-[0.6rem] uppercase tracking-[0.3em] transition ${
            beat.activationMode === m
              ? 'border-accent/60 bg-paper-800/60 text-ash-100'
              : 'border-paper-800/60 bg-paper-950/40 text-ash-500 hover:border-ash-700 hover:text-ash-300 disabled:cursor-not-allowed'
          }`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

function BroadcastPublishModal({
  beat,
  pending,
  onConfirm,
  onCancel,
}: {
  beat: AdminBeat;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);
  const [eligibleError, setEligibleError] = useState<string | null>(null);
  const [bonds, setBonds] = useState<AdminBondSummary[]>([]);
  const [previewBondId, setPreviewBondId] = useState<string>('');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<PreviewBroadcastResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await getBroadcastPreview(beat.id);
        if (!cancelled) setEligibleCount(p.eligibleBonds);
      } catch (err) {
        if (!cancelled) setEligibleError((err as Error).message);
      }
      try {
        const list = await listAdminBonds();
        if (!cancelled) {
          setBonds(list);
          if (list[0]) setPreviewBondId(list[0].id);
        }
      } catch {
        // ignore — preview is optional
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [beat.id]);

  async function handlePreview() {
    if (!previewBondId) return;
    setPreviewing(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const result = await previewBroadcastForBond(beat.id, previewBondId);
      setPreview(result);
    } catch (err) {
      setPreviewError((err as Error).message);
    } finally {
      setPreviewing(false);
    }
  }

  const previewBond = bonds.find((b) => b.id === previewBondId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-paper-950/85 p-6 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-sm border border-paper-700 bg-paper-900 p-6 lg:p-7">
        <h3 className="font-display text-ash-50 text-xl">Publish {beat.beatId} as broadcast?</h3>
        <p className="mt-2 font-body text-ash-300 text-sm leading-relaxed">
          Publishing this beat will enqueue an unsolicited Ember letter to every eligible bond
          (those not currently in <code className="text-ash-200">unstable_bond</code>). Each
          letter respects the per-bond cadence delay (30 min – several hours), so the wave
          arrives in its own time, not in lockstep.
        </p>

        <div className="mt-5 rounded-sm border border-accent/30 bg-paper-800/40 p-3 font-display text-ash-100 text-sm">
          {eligibleError ? (
            <span className="text-ash-500">Could not preview: {eligibleError}</span>
          ) : eligibleCount === null ? (
            <span className="text-ash-500">Counting eligible bonds…</span>
          ) : (
            <span>
              <span className="text-accent-strong text-base">{eligibleCount}</span> letter
              {eligibleCount === 1 ? '' : 's'} will be enqueued.
            </span>
          )}
        </div>

        <section className="mt-6 rounded-sm border border-paper-800/60 bg-paper-950/40 p-4">
          <header>
            <p className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.32em]">
              Dry-run on a single bond
            </p>
            <p className="mt-1 font-body text-ash-400 text-xs italic leading-relaxed">
              Generate the letter for one bond without enqueuing or persisting anything. Iterate
              the lore until the voice + verifier outcome look right, then publish for real.
              Tokens are spent on each preview.
            </p>
          </header>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="mb-1 block font-display text-[0.6rem] text-ash-400 uppercase tracking-[0.32em]">
                Preview on
              </label>
              <select
                value={previewBondId}
                onChange={(e) => setPreviewBondId(e.target.value)}
                disabled={bonds.length === 0 || previewing}
                className="w-full rounded-sm border border-paper-700 bg-paper-900 px-3 py-2 font-body text-ash-100 text-sm focus:border-accent/60 focus:outline-none disabled:opacity-50"
              >
                {bonds.length === 0 && <option value="">No bonds available</option>}
                {bonds.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.emberName} ({b.primaryElement})
                    {b.relationshipState ? ` · ${b.relationshipState}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handlePreview}
              disabled={previewing || !previewBondId}
              className="rounded-sm border border-paper-700 bg-paper-900 px-4 py-2 font-display text-ash-100 text-xs uppercase tracking-[0.32em] transition hover:border-accent/60 hover:text-ash-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {previewing ? 'Generating…' : 'Preview'}
            </button>
          </div>

          {previewError && (
            <p className="mt-3 font-body text-ash-300 text-sm italic">Preview failed: {previewError}</p>
          )}

          {preview && (
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3 font-display text-[0.6rem] uppercase tracking-[0.32em]">
                <span
                  className={`rounded-sm border px-2 py-0.5 ${
                    preview.outcome === 'success'
                      ? 'border-accent/50 text-accent-strong'
                      : 'border-ash-600 text-ash-400'
                  }`}
                >
                  {preview.outcome}
                </span>
                <span className="text-ash-500">type: {preview.letterType}</span>
                {preview.retries > 0 && (
                  <span className="text-ash-500">retries: {preview.retries}</span>
                )}
                {previewBond && (
                  <span className="text-ash-500">
                    as {previewBond.emberName}
                  </span>
                )}
              </div>

              {preview.violations.length > 0 && (
                <ul className="space-y-1 rounded-sm border border-paper-800/60 bg-paper-950/40 p-3">
                  {preview.violations.map((v, idx) => (
                    <li key={`${v.code}-${idx}`} className="font-body text-ash-300 text-xs">
                      <span className="font-display text-[0.58rem] text-accent uppercase tracking-[0.3em]">
                        {v.code}
                      </span>
                      <span className="ml-2 italic">{v.reason}</span>
                    </li>
                  ))}
                </ul>
              )}

              <article
                className={`whitespace-pre-line rounded-sm border p-4 font-body text-sm leading-relaxed ${
                  preview.outcome === 'success'
                    ? 'border-accent/30 bg-paper-800/40 text-ash-100'
                    : 'border-ash-700/40 bg-paper-950/40 text-ash-300'
                }`}
              >
                {preview.body}
              </article>

              {preview.outcome === 'fallback' && preview.lastDraft && (
                <details className="rounded-sm border border-paper-800/50 bg-paper-950/30 p-3">
                  <summary className="cursor-pointer font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em] hover:text-ash-300">
                    Show rejected draft
                  </summary>
                  <article className="mt-2 whitespace-pre-line font-body text-ash-400 text-xs leading-relaxed">
                    {preview.lastDraft}
                  </article>
                </details>
              )}
            </div>
          )}
        </section>

        <p className="mt-4 font-body text-ash-500 text-xs italic">
          Idempotent: bonds that already received this beat will be skipped (you cannot
          accidentally re-broadcast by unpublishing and republishing).
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="font-display text-ash-400 text-xs uppercase tracking-[0.32em] transition hover:text-ash-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending || eligibleCount === null}
            className="rounded-sm border border-accent/50 bg-paper-800 px-4 py-2 font-display text-ash-100 text-xs uppercase tracking-[0.32em] transition hover:border-accent-strong/80 hover:bg-paper-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? 'Publishing…' : 'Publish + broadcast'}
          </button>
        </div>
      </div>
    </div>
  );
}

const VALID_BIASES: { value: BeatEditPayload['letterTypeBias']; label: string }[] = [
  { value: null, label: 'no bias' },
  { value: 'memory_shard', label: 'memory shard' },
  { value: 'public_event_reaction', label: 'public event reaction' },
  { value: 'dream', label: 'dream' },
  { value: 'deep_form', label: 'deep form' },
  { value: 'choice', label: 'choice' },
];

function EditBeatModal({
  beat,
  pending,
  onSave,
  onCancel,
}: {
  beat: AdminBeat;
  pending: boolean;
  onSave: (payload: BeatEditPayload) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(beat.title);
  const [summary, setSummary] = useState(beat.summary);
  const [hint, setHint] = useState(beat.privateReactionHint);
  const [bias, setBias] = useState<BeatEditPayload['letterTypeBias']>(
    beat.letterTypeBias as BeatEditPayload['letterTypeBias'],
  );
  const [triggersUnstable, setTriggersUnstable] = useState(beat.triggersUnstable);
  const [postUrl, setPostUrl] = useState(beat.canonicalPostUrl ?? '');
  const [error, setError] = useState<string | null>(null);

  const canSave =
    title.trim().length > 0 &&
    summary.trim().length > 0 &&
    hint.trim().length > 0 &&
    !pending;

  function handleSubmit() {
    setError(null);
    const trimmedUrl = postUrl.trim();
    if (trimmedUrl) {
      try {
        new URL(trimmedUrl);
      } catch {
        setError('canonical post URL is not a valid URL');
        return;
      }
    }
    onSave({
      title: title.trim(),
      summary: summary.trim(),
      privateReactionHint: hint.trim(),
      letterTypeBias: bias,
      triggersUnstable,
      canonicalPostUrl: trimmedUrl ? trimmedUrl : null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-paper-950/85 p-6 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-sm border border-paper-700 bg-paper-900 p-6 lg:p-7">
        <header className="mb-4">
          <p className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
            edit {beat.beatId} · draft
          </p>
          <h3 className="mt-1 font-display text-ash-50 text-xl">Edit beat lore</h3>
          <p className="mt-2 font-body text-ash-400 text-xs italic leading-relaxed">
            Once published, fields lock — letters already shipped to bonds carry the values that
            were live at publish time. Unpublish first if you need to revise after.
          </p>
        </header>

        <div className="space-y-5">
          <Field label="Title (in-world name of the event)">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              className="w-full rounded-sm border border-paper-700 bg-paper-950/40 px-3 py-2 font-body text-ash-100 text-sm focus:border-accent/60 focus:outline-none"
            />
          </Field>

          <Field
            label="Summary (the entity of the world that occurred — what the Ember senses)"
            hint="Speaks to the LLM directly. Avoid mentioning Instagram, posts, the platform — write the lore as it exists in-world."
          >
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full resize-none rounded-sm border border-paper-700 bg-paper-950/40 px-3 py-2 font-body text-ash-100 text-sm leading-relaxed focus:border-accent/60 focus:outline-none"
            />
          </Field>

          <Field
            label="Private reaction hint (internal guidance for the Ember)"
            hint="Never shown to the user. Think: 'You feel something old stir near the dark; be quietly attentive without naming it.'"
          >
            <textarea
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full resize-none rounded-sm border border-paper-700 bg-paper-950/40 px-3 py-2 font-body text-ash-100 text-sm leading-relaxed focus:border-accent/60 focus:outline-none"
            />
          </Field>

          <Field label="Letter-type bias (next reply tilts toward this type, if set)">
            <select
              value={bias ?? ''}
              onChange={(e) => setBias((e.target.value || null) as BeatEditPayload['letterTypeBias'])}
              className="w-full rounded-sm border border-paper-700 bg-paper-950/40 px-3 py-2 font-body text-ash-100 text-sm focus:border-accent/60 focus:outline-none"
            >
              {VALID_BIASES.map((opt) => (
                <option key={opt.value ?? 'none'} value={opt.value ?? ''}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={triggersUnstable}
              onChange={(e) => setTriggersUnstable(e.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            <span className="font-display text-[0.7rem] text-ash-300 uppercase tracking-[0.3em]">
              triggers unstable bond on choice resolution
            </span>
          </label>

          <Field
            label="Canonical post URL (the IG/web post telling this lore publicly — optional)"
            hint="If empty, this beat is internal only — no public surface, just Ember context."
          >
            <input
              type="url"
              value={postUrl}
              onChange={(e) => setPostUrl(e.target.value)}
              placeholder="https://instagram.com/p/…"
              className="w-full rounded-sm border border-paper-700 bg-paper-950/40 px-3 py-2 font-body text-ash-100 text-sm focus:border-accent/60 focus:outline-none"
            />
          </Field>

          {error && (
            <p className="font-body text-ash-300 text-sm italic">{error}</p>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="font-display text-ash-400 text-xs uppercase tracking-[0.32em] transition hover:text-ash-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave}
            className="rounded-sm border border-accent/50 bg-paper-800 px-4 py-2 font-display text-ash-100 text-xs uppercase tracking-[0.32em] transition hover:border-accent-strong/80 hover:bg-paper-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block font-display text-[0.6rem] text-ash-400 uppercase tracking-[0.32em]">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 font-body text-ash-500 text-xs italic leading-relaxed">{hint}</p>}
    </div>
  );
}

function PublishResultBanner({
  result,
  onDismiss,
}: {
  result: { beatId: string; enqueued: number; skippedExisting: number };
  onDismiss: () => void;
}) {
  return (
    <div className="fixed right-6 bottom-6 z-50 max-w-sm rounded-sm border border-accent/40 bg-paper-900 p-4 shadow-lg">
      <p className="font-display text-[0.6rem] text-accent-strong uppercase tracking-[0.32em]">
        {result.beatId} broadcast
      </p>
      <p className="mt-2 font-body text-ash-100 text-sm">
        <span className="text-accent-strong">{result.enqueued}</span> letter
        {result.enqueued === 1 ? '' : 's'} enqueued
        {result.skippedExisting > 0 ? ` (skipped ${result.skippedExisting} already broadcast)` : ''}.
      </p>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-3 font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em] hover:text-ash-200"
      >
        Dismiss
      </button>
    </div>
  );
}

function StatusPill({ status }: { status: AdminBeat['status'] }) {
  const tone =
    status === 'published'
      ? 'border-accent/50 text-accent-strong'
      : status === 'draft'
        ? 'border-ash-600/60 text-ash-300'
        : 'border-ash-700/40 text-ash-500';
  return (
    <span
      className={`rounded-full border px-2 py-0.5 font-display text-[0.55rem] uppercase tracking-[0.3em] ${tone}`}
    >
      {status}
    </span>
  );
}

function UnpublishModal({
  beat,
  pending,
  onConfirm,
  onCancel,
}: {
  beat: AdminBeat;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-paper-950/85 p-6 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-sm border border-paper-700 bg-paper-900 p-6 lg:p-7">
        <h3 className="font-display text-ash-50 text-xl">Unpublish {beat.beatId}?</h3>
        <p className="mt-2 font-body text-ash-300 text-sm leading-relaxed">
          This flips the beat back to draft. It will stop biasing letter generation for active
          bonds. The published timestamp stays as the last live record; re-publishing recomputes
          fresh.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="font-display text-ash-400 text-xs uppercase tracking-[0.32em] transition hover:text-ash-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="rounded-sm border border-accent/50 bg-paper-800 px-4 py-2 font-display text-ash-100 text-xs uppercase tracking-[0.32em] transition hover:border-accent-strong/80 hover:bg-paper-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pending ? 'Unpublishing…' : 'Unpublish'}
          </button>
        </div>
      </div>
    </div>
  );
}
